import { bookTemplates, type TemplateId } from "@lifescapsule/contracts";

export const REDEMPTION_DRAFT_STORAGE_KEY = "lifescapsule:redemption-draft";
export const REDEMPTION_DRAFT_LOCK_NAME = "lifescapsule:redemption-draft:exclusive-claim";
export const REDEMPTION_DRAFT_MAX_AGE_MS = 15 * 60 * 1_000;

export type RedemptionDraftIdentity = Readonly<{
  draftId: string;
  challengeToken: string;
}>;

type StoredRedemptionDraft = RedemptionDraftIdentity & {
  schemaVersion: 3;
  state: "committed";
  expiresAt: number;
  accountBinding: string;
  offerName: string;
  kind?: "SHARED_TEST_GIFT" | "VOUCHER";
  reusable?: boolean;
  selectionRequired?: boolean;
  test: boolean;
  templateId: TemplateId;
  storytellerName: string;
  /** What the voucher includes, as its check said, so the tab finishing sign-in says it too. */
  includedPrintCopies?: 0 | 1;
  /** The books a choose-a-book voucher offers, so that tab can still offer them. */
  allowedTemplateIds?: TemplateId[];
};

type PendingStoredRedemptionDraft = Omit<StoredRedemptionDraft, "state"> & {
  state: "pending";
};

export type RedemptionDraftInput = Omit<
  StoredRedemptionDraft,
  "schemaVersion" | "state" | "draftId" | "expiresAt" | "accountBinding" | "test"
> & {
  challengeExpiresAt: string;
  test?: boolean;
};

export type RedemptionDraftResult =
  | {
      status:
        | "empty"
        | "expired"
        | "account_mismatch"
        | "account_unverified"
        | "coordination_unavailable";
    }
  | { status: "ready"; draft: StoredRedemptionDraft };

export type RedemptionDraftSaveResult = RedemptionDraftIdentity | false;

type ExclusiveLockManager = {
  request<T>(
    name: string,
    options: { mode: "exclusive" },
    callback: () => T | PromiseLike<T>,
  ): Promise<T>;
};

type CoordinatedResult<T> =
  | { status: "complete"; value: T }
  | { status: "coordination_unavailable" };

function storage() {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function lockManager(): ExclusiveLockManager | undefined {
  try {
    const locks = globalThis.navigator?.locks as ExclusiveLockManager | undefined;
    return locks && typeof locks.request === "function" ? locks : undefined;
  } catch {
    return undefined;
  }
}

async function withExclusiveDraftLock<T>(
  callback: () => T | PromiseLike<T>,
): Promise<CoordinatedResult<T>> {
  const locks = lockManager();
  if (!locks) return { status: "coordination_unavailable" };
  try {
    return {
      status: "complete",
      value: await locks.request(REDEMPTION_DRAFT_LOCK_NAME, { mode: "exclusive" }, callback),
    };
  } catch {
    return { status: "coordination_unavailable" };
  }
}

function normalizeEmail(email: string) {
  const normalized = email.trim().toLowerCase();
  return normalized.includes("@") && normalized.length <= 320 ? normalized : "";
}

async function accountBinding(email: string) {
  const normalized = normalizeEmail(email);
  if (!normalized || !crypto.subtle) return null;
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(normalized));
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/u, "");
}

function validDraftId(value: unknown): value is string {
  return (
    typeof value === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(value)
  );
}

function validDraftShape(
  value: unknown,
): value is StoredRedemptionDraft | PendingStoredRedemptionDraft {
  if (!value || typeof value !== "object") return false;
  const draft = value as Partial<StoredRedemptionDraft | PendingStoredRedemptionDraft>;
  return (
    draft.schemaVersion === 3 &&
    (draft.state === "pending" || draft.state === "committed") &&
    validDraftId(draft.draftId) &&
    typeof draft.challengeToken === "string" &&
    /^[A-Za-z0-9_-]{43}$/u.test(draft.challengeToken) &&
    typeof draft.expiresAt === "number" &&
    Number.isFinite(draft.expiresAt) &&
    typeof draft.accountBinding === "string" &&
    /^[A-Za-z0-9_-]{43}$/u.test(draft.accountBinding) &&
    typeof draft.offerName === "string" &&
    draft.offerName.length > 0 &&
    draft.offerName.length <= 160 &&
    (draft.kind === undefined || ["SHARED_TEST_GIFT", "VOUCHER"].includes(draft.kind)) &&
    (draft.reusable === undefined || typeof draft.reusable === "boolean") &&
    (draft.selectionRequired === undefined || typeof draft.selectionRequired === "boolean") &&
    typeof draft.test === "boolean" &&
    typeof draft.templateId === "string" &&
    bookTemplates.some((template) => template.id === draft.templateId) &&
    typeof draft.storytellerName === "string" &&
    draft.storytellerName.length <= 100 &&
    (draft.includedPrintCopies === undefined ||
      draft.includedPrintCopies === 0 ||
      draft.includedPrintCopies === 1) &&
    (draft.allowedTemplateIds === undefined ||
      (Array.isArray(draft.allowedTemplateIds) &&
        draft.allowedTemplateIds.length <= 50 &&
        draft.allowedTemplateIds.every((id) =>
          bookTemplates.some((template) => template.id === id),
        )))
  );
}

function validStoredDraft(value: unknown): value is StoredRedemptionDraft {
  return validDraftShape(value) && value.state === "committed";
}

function parseStoredDraft(serialized: string | null) {
  if (!serialized) return null;
  try {
    const value: unknown = JSON.parse(serialized);
    return validDraftShape(value) ? value : null;
  } catch {
    return null;
  }
}

function sameDraftIdentity(
  value: StoredRedemptionDraft | PendingStoredRedemptionDraft | null,
  identity: RedemptionDraftIdentity,
) {
  return value?.draftId === identity.draftId && value.challengeToken === identity.challengeToken;
}

type DraftClearExpectation =
  | { kind: "identity"; value: RedemptionDraftIdentity }
  | { kind: "serialized"; value: string };

function observedDraftForClear(): DraftClearExpectation | null {
  const target = storage();
  if (!target) return null;
  try {
    const serialized = target.getItem(REDEMPTION_DRAFT_STORAGE_KEY);
    if (!serialized) return null;
    const draft = parseStoredDraft(serialized);
    return draft
      ? {
          kind: "identity",
          value: { draftId: draft.draftId, challengeToken: draft.challengeToken },
        }
      : { kind: "serialized", value: serialized };
  } catch {
    return null;
  }
}

/**
 * Removes only the exact draft observed by the caller. A no-argument call snapshots
 * the current identity immediately for legacy sign-out callers, then compares again
 * while holding the same origin-wide lock used by save and take.
 */
export function clearRedemptionDraft(
  expectedIdentity: RedemptionDraftIdentity | null = null,
): Promise<boolean> {
  const observed: DraftClearExpectation | null = expectedIdentity
    ? { kind: "identity", value: expectedIdentity }
    : observedDraftForClear();
  if (!observed) return Promise.resolve(false);
  return withExclusiveDraftLock(() => {
    const target = storage();
    if (!target) throw new Error("Redemption draft storage is unavailable.");
    const serialized = target.getItem(REDEMPTION_DRAFT_STORAGE_KEY);
    const matches =
      observed.kind === "identity"
        ? sameDraftIdentity(parseStoredDraft(serialized), observed.value)
        : serialized === observed.value;
    if (!matches) return false;
    target.removeItem(REDEMPTION_DRAFT_STORAGE_KEY);
    if (target.getItem(REDEMPTION_DRAFT_STORAGE_KEY) !== null)
      throw new Error("Redemption draft could not be safely cleared.");
    return true;
  }).then((result) => result.status === "complete" && result.value);
}

function rollbackDraftWrite(target: Storage, identity: RedemptionDraftIdentity) {
  try {
    const current = parseStoredDraft(target.getItem(REDEMPTION_DRAFT_STORAGE_KEY));
    if (!sameDraftIdentity(current, identity)) return;
  } catch {
    // The exclusive lock makes this the only cooperating writer. If verification
    // itself is unavailable immediately after our write, remove our own write.
  }
  try {
    target.removeItem(REDEMPTION_DRAFT_STORAGE_KEY);
  } catch {
    // A pending record is deliberately non-restorable if storage cannot remove it.
  }
}

export async function saveRedemptionDraft(
  input: RedemptionDraftInput,
  intendedAccountEmail: string,
): Promise<RedemptionDraftSaveResult> {
  const binding = await accountBinding(intendedAccountEmail);
  const challengeExpiry = Date.parse(input.challengeExpiresAt);
  const expiresAt = Math.min(challengeExpiry, Date.now() + REDEMPTION_DRAFT_MAX_AGE_MS);
  if (!binding || !Number.isFinite(challengeExpiry) || expiresAt <= Date.now()) return false;
  if (typeof crypto.randomUUID !== "function") return false;
  const identity: RedemptionDraftIdentity = {
    draftId: crypto.randomUUID(),
    challengeToken: input.challengeToken,
  };
  const draft: StoredRedemptionDraft = {
    schemaVersion: 3,
    state: "committed",
    ...identity,
    expiresAt,
    accountBinding: binding,
    offerName: input.offerName.trim().slice(0, 160),
    ...(input.kind ? { kind: input.kind } : {}),
    ...(input.reusable === undefined ? {} : { reusable: input.reusable }),
    ...(input.selectionRequired === undefined
      ? {}
      : { selectionRequired: input.selectionRequired }),
    test: input.test ?? true,
    templateId: input.templateId,
    storytellerName: input.storytellerName.trim().slice(0, 100),
    ...(input.includedPrintCopies === undefined
      ? {}
      : { includedPrintCopies: input.includedPrintCopies }),
    ...(input.allowedTemplateIds ? { allowedTemplateIds: input.allowedTemplateIds } : {}),
  };
  if (!validStoredDraft(draft)) return false;
  const pending: PendingStoredRedemptionDraft = { ...draft, state: "pending" };
  const pendingSerialized = JSON.stringify(pending);
  const committedSerialized = JSON.stringify(draft);
  const result = await withExclusiveDraftLock(() => {
    const target = storage();
    if (!target) throw new Error("Redemption draft storage is unavailable.");
    try {
      target.setItem(REDEMPTION_DRAFT_STORAGE_KEY, pendingSerialized);
      if (target.getItem(REDEMPTION_DRAFT_STORAGE_KEY) !== pendingSerialized)
        throw new Error("Pending redemption draft storage could not be verified.");
      target.setItem(REDEMPTION_DRAFT_STORAGE_KEY, committedSerialized);
      if (target.getItem(REDEMPTION_DRAFT_STORAGE_KEY) !== committedSerialized)
        throw new Error("Redemption draft storage could not be verified.");
      return identity;
    } catch (cause) {
      rollbackDraftWrite(target, identity);
      throw cause;
    }
  });
  return result.status === "complete" ? result.value : false;
}

export async function takeRedemptionDraft(
  authenticatedAccountEmail: string | null | undefined,
): Promise<RedemptionDraftResult> {
  const claim = await withExclusiveDraftLock(() => {
    const target = storage();
    if (!target) throw new Error("Redemption draft storage is unavailable.");
    const serialized = target.getItem(REDEMPTION_DRAFT_STORAGE_KEY);
    if (!serialized) return null;
    target.removeItem(REDEMPTION_DRAFT_STORAGE_KEY);
    if (target.getItem(REDEMPTION_DRAFT_STORAGE_KEY) !== null)
      throw new Error("Redemption draft could not be exclusively claimed.");
    return serialized;
  });
  if (claim.status === "coordination_unavailable") return claim;
  const serialized = claim.value;
  if (!serialized) return { status: "empty" };
  let value: unknown;
  try {
    value = JSON.parse(serialized);
  } catch {
    return { status: "empty" };
  }
  if (!validStoredDraft(value)) return { status: "empty" };
  if (value.expiresAt <= Date.now()) return { status: "expired" };
  if (!authenticatedAccountEmail) return { status: "account_unverified" };
  const binding = await accountBinding(authenticatedAccountEmail);
  if (!binding) return { status: "account_unverified" };
  if (binding !== value.accountBinding) return { status: "account_mismatch" };
  return { status: "ready", draft: value };
}
